import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../Toast';
import { EmptyState } from '../EmptyState';
import { formatCents } from '../../lib/billing-admin-queries';
import {
  fetchLiveLotsWithBids,
  adminRemoveBid,
  type AdminLiveLot,
  type AdminLotBid,
} from '../../lib/saleroom-admin-queries';

/**
 * Admin bid management for LIVE lots. Lists every open lot with its full bid
 * list (highest first); each bid has a Remove action (confirmed) that voids it.
 * Removing the top bid reverts the lot to the next-highest — the server derives
 * the current bid from the remaining bids, so there's nothing to recompute here.
 *
 * Fetches on first activation (the panel is kept mounted but hidden).
 */
export function SaleroomLiveBids({ active }: { active: boolean }) {
  const toast = useToast();
  const [lots, setLots] = useState<AdminLiveLot[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyBid, setBusyBid] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setLots(await fetchLiveLotsWithBids());
      setLoaded(true);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load live lots.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (active && !loaded && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  async function handleRemove(lot: AdminLiveLot, bid: AdminLotBid) {
    const ok = window.confirm(
      `Remove the ${formatCents(bid.amount_cents)} bid` +
      `${bid.bidder_name ? ` from ${bid.bidder_name}` : ''} on LOT ${lot.lot_number} — ${lot.lot_name}?\n\n` +
      `The lot falls back to the next-highest remaining bid. This can't be undone.`,
    );
    if (!ok) return;
    setBusyBid(bid.id);
    try {
      await adminRemoveBid(bid.id);
      toast.success('Bid removed.');
      await load();
    } catch (err: any) {
      const msg = err?.message === 'auction_not_live'
        ? 'That lot is no longer live — its bids are locked.'
        : err?.message ?? 'Could not remove the bid.';
      toast.error(msg);
    } finally {
      setBusyBid(null);
    }
  }

  return (
    <div style={{ marginBottom: 'var(--space-12)' }}>
      <div className="admin__panel-head">
        <h2>Live lots — bids</h2>
        <button type="button" className="btn btn--ghost btn--sm" onClick={load} disabled={loading}>
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      <p className="admin__note">
        Every lot that's currently live. Remove a bid to void it — the lot falls back to the
        next-highest remaining bid. Bids can only be removed while a lot is live.
      </p>

      {!loaded && loading ? (
        <p className="admin__note">Loading…</p>
      ) : lots.length === 0 ? (
        <EmptyState
          tone="compact"
          align="start"
          eyebrow="Quiet"
          headline="No live lots right now."
          body="Lots show here while their bidding window is open."
        />
      ) : (
        lots.map((lot) => (
          <div key={lot.post_id} style={{ marginBottom: 'var(--space-8)' }}>
            <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--t-base)' }}>
              <strong>LOT {lot.lot_number}</strong>{' '}
              {lot.post_slug ? <Link to={`/p/${lot.post_slug}`}>{lot.lot_name}</Link> : lot.lot_name}
              <span style={{ color: 'var(--text-soft)', fontWeight: 400 }}>
                {' · '}current {formatCents(lot.current_high_amount)}
                {' · '}{lot.bids.length} {lot.bids.length === 1 ? 'bid' : 'bids'}
              </span>
            </h3>
            {lot.bids.length === 0 ? (
              <p className="admin__note" style={{ margin: 0 }}>No bids yet.</p>
            ) : (
              <div className="admin__table-wrap">
                <table className="admin__table">
                  <thead>
                    <tr>
                      <th>Bidder</th>
                      <th>Amount</th>
                      <th>Placed</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lot.bids.map((bid, i) => (
                      <tr key={bid.id}>
                        <td>
                          {bid.bidder_name || '—'}
                          {bid.bidder_handle && (
                            <span style={{ display: 'block', color: 'var(--text-soft)', fontSize: '0.85em' }}>
                              @{bid.bidder_handle}
                            </span>
                          )}
                        </td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatCents(bid.amount_cents)}
                          {i === 0 && <span className="pill pill--active" style={{ marginLeft: 8 }}>Top</span>}
                        </td>
                        <td style={{ color: 'var(--text-soft)' }}>
                          {new Date(bid.created_at).toLocaleString()}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => handleRemove(lot, bid)}
                            disabled={busyBid === bid.id}
                          >
                            {busyBid === bid.id ? '…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
